import { gql } from "@apollo/client";
import moment from "moment";

import { API } from "api/API";
import axiosInstance from "api/axiosInstance";
import { getToken } from "utilities/storageUtilities";

const APIInstance = new API();

/**
 * Third-party API-key lifecycle (OS-2550), CBO customer-admin side.
 *
 * Reads + revoke go through Hasura (row-scoped by the `administrator` SELECT/UPDATE permissions
 * on `third_party_api_key`, filter `corporate_id _eq X-Hasura-Corporate-Id`) — so no corporate_id
 * variable is needed here. Mint (generate/rotate) goes through the `/corporate/api-keys` backend
 * endpoints because the key must be minted server-side and stored SHA-256-hashed; the backend
 * derives corporate_id from the JWT, so it is never sent in the body.
 *
 * The optional `$where` is the "Active APIs" filter, built in ApiKeysEntity and ANDed with the
 * permission's corporate_id scoping — filtering happens at the query level, not in the client.
 */
export const getThirdPartyApiKeys = gql`
    query GetThirdPartyApiKeys($where: third_party_api_key_bool_exp) {
        third_party_api_key(where: $where, order_by: { created_at: desc }) {
            third_party_api_key_id
            key_prefix
            label
            created_at
            expires_at
            last_used_at
            revoked_at
        }
    }
`;

export const getThirdPartyApiCorporateConfig = gql`
    query GetThirdPartyApiCorporateConfig {
        third_party_api_corporate_config {
            corporate_id
            is_enabled
            max_active_api_keys
        }
    }
`;

// Revoke + its actor-bearing audit row in ONE mutation document (one Hasura transaction),
// mirroring the MBO revoke (AC-8). `revoked_at _is_null` makes re-revoking a stale row a no-op
// (affected_rows 0 — the view surfaces it) instead of clobbering the original timestamp; the
// audit row's corporate_id/changed_by_user_id are session-preset by the insert permission.
export const revokeThirdPartyApiKey = gql`
    mutation RevokeThirdPartyApiKey($id: bigint!, $revoked_at: timestamp!, $key_id_text: String!) {
        update_third_party_api_key(
            where: { third_party_api_key_id: { _eq: $id }, revoked_at: { _is_null: true } }
            _set: { revoked_at: $revoked_at }
        ) {
            affected_rows
        }
        insert_third_party_api_audit_log(objects: [{ action: "api_key_revoked", new_value: $key_id_text }]) {
            affected_rows
        }
    }
`;

export interface IThirdPartyApiKey {
    third_party_api_key_id: string;
    key_prefix: string;
    label: string | null;
    created_at: string;
    expires_at: string;
    last_used_at: string | null;
    revoked_at: string | null;
}

/**
 * A key is active iff it is neither revoked nor expired — the same definition the backend
 * counts against the per-Corporation cap (OS-2552) and the status column derives. Single
 * source of truth for the "Active APIs" filter and the active-key count that gates Generate.
 */
export const isApiKeyActive = (key: IThirdPartyApiKey): boolean =>
    !key.revoked_at && moment(key.expires_at).isAfter(moment());

export interface IGenerateApiKeyResponse {
    third_party_api_key_id: number;
    key_prefix: string;
    label: string | null;
    created_at: string;
    expires_at: string;
    /** Raw bearer credential — present only in this response, shown to the user exactly once. */
    api_key: string;
}

export const generateApiKey = async (payload: {
    label: string;
    expires_at?: string;
}): Promise<IGenerateApiKeyResponse> => {
    const currentToken = getToken();
    const url = `${APIInstance.api_root}/corporate/api-keys`;
    const response = await axiosInstance.post(url, payload, {
        headers: {
            authorization: `Bearer ${currentToken}`,
            "content-type": "application/json",
        },
    });
    return response.data;
};

export const rotateApiKey = async (
    apiKeyId: string,
    payload: { expires_at?: string },
): Promise<IGenerateApiKeyResponse> => {
    const currentToken = getToken();
    const url = `${APIInstance.api_root}/corporate/api-keys/${apiKeyId}/rotate`;
    const response = await axiosInstance.post(url, payload, {
        headers: {
            authorization: `Bearer ${currentToken}`,
            "content-type": "application/json",
        },
    });
    return response.data;
};
